// JOBE-0106: CompactEntityList — small compact list reusing
// useEntityBrowserState. Renders only the first ~5 rows from the
// query — used by the Overview body's Tasks / Recent documents
// lanes to mirror the same namespaced URL state as the full sub-tab
// instance without spending the screen real estate on the grid.
//
// Per spec §"URL state composition": the Overview lane and the Tasks
// sub-tab share the `tasks` namespace; switching tabs inherits the
// same chip state. The `defaultType` is threaded as a `defaultChips`
// seed (not an anchor) for parity with the full sub-tab — both lanes
// see `type=<defaultType>` as a removable chip in the namespaced URL
// state. Reviewer concern: anchor-vs-defaultChips inconsistency.

import React from 'react';
import { useEntityBrowserState } from '../../entitybrowser/useEntityBrowserState.js';

export function CompactEntityList({
  projectURN,
  namespace,
  defaultType,
  limit = 5,
  title,
  emptyLabel,
}) {
  const defaultChips = defaultType
    ? [{ kind: 'predicate', friendlyToken: 'type', wirePredicate: 'type', values: [defaultType] }]
    : undefined;
  const browser = useEntityBrowserState({
    anchor: { inProject: projectURN },
    urlStateNamespace: namespace,
    defaultChips,
  });
  const { rows, loadState, error } = browser;
  const visible = Array.isArray(rows) ? rows.slice(0, limit) : [];

  return (
    <section
      className="compact-entity-list"
      data-testid={`compact-${namespace}`}
    >
      <header className="lane-header">
        <h3 className="lane-title">{title}</h3>
      </header>
      {loadState === 'loading' ? (
        <p className="muted">loading…</p>
      ) : null}
      {loadState === 'error' ? (
        <p className="muted">
          error{error?.status ? ` (${error.status})` : ''}
        </p>
      ) : null}
      {loadState === 'ready' && visible.length === 0 ? (
        <p className="muted">{emptyLabel || 'No items.'}</p>
      ) : null}
      {visible.length > 0 ? (
        <ul className="compact-entity-rows">
          {visible.map((row) => (
            <li key={row.urn} className="compact-entity-row" data-urn={row.urn}>
              <span className="compact-entity-name">{row.name || row.urn}</span>
              {row.kind ? (
                <span className="compact-entity-kind mono"> · {row.kind}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
